import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Feather } from "lucide-react";
import { getLang } from "../i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

const T = {
  en: {
    title: "Nonlocalfield Notes",
    desc:
      "Receive occasional Field Notes by email — quiet reflections on nondual spaceholding, subtle mechanics, and the science of unconditioned presence. Offered freely, sent rarely.",
    emailLabel: "Email",
    submit: "Subscribe to Field Notes",
    note: "Already have the Master Monograph? This signs you up for Field Notes only — no download needed. Your email rests with us in confidence and is never shared.",
    pending: "Almost there — please tap the link in our confirmation email to begin receiving Field Notes.",
    already: "You already receive our Field Notes. Thank you for being here.",
    invalid: "Please enter a valid email address.",
    error: "Something interrupted the request. Please try again in a moment.",
  },
  de: {
    title: "Nonlocalfield Notes",
    desc:
      "Erhalte gelegentliche Field Notes per E-Mail — stille Reflexionen über nonduales Spaceholding, subtile Mechanik und die Wissenschaft der bedingungslosen Präsenz. Frei angeboten, selten gesendet.",
    emailLabel: "E-Mail",
    submit: "Field Notes abonnieren",
    note: "Du hast die Master-Monographie bereits? Dies meldet dich nur für die Field Notes an — kein Download nötig. Deine E-Mail-Adresse ruht vertraulich bei uns und wird niemals weitergegeben.",
    pending: "Fast geschafft — bitte tippe auf den Link in unserer Bestätigungs-E-Mail, um die Field Notes zu erhalten.",
    already: "Du erhältst unsere Field Notes bereits. Danke, dass du hier bist.",
    invalid: "Bitte gib eine gültige E-Mail-Adresse ein.",
    error: "Etwas hat die Anfrage unterbrochen. Bitte versuche es gleich noch einmal.",
  },
};

const FieldNotesSubscribeModal = ({ children }) => {
  const location = useLocation();
  const lang = getLang(location.pathname);
  const t = T[lang];
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    if (!isEmail(email)) {
      toast.error(t.invalid);
      return;
    }
    setBusy(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const { data } = await axios.post(`${API}/field-notes/subscribe`, {
        email: email.trim(),
        lang,
        origin: window.location.origin,
      });
      toast.success(data?.notes_status === "already" ? t.already : t.pending);
      setOpen(false);
      setEmail("");
    } catch (err) {
      toast.error(t.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="monograph-modal" data-testid="field-notes-subscribe-modal">
        <DialogHeader>
          <div className="monograph-mark" aria-hidden="true">
            <Feather size={20} />
          </div>
          <DialogTitle className="monograph-title">{t.title}</DialogTitle>
          <DialogDescription className="monograph-desc">{t.desc}</DialogDescription>
        </DialogHeader>

        <div className="monograph-body">
          <label className="field-label" htmlFor="fn-email">{t.emailLabel}</label>
          <Input
            id="fn-email"
            type="email"
            data-testid="field-notes-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
          <button
            type="button"
            className="btn-gold w-full monograph-submit"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="field-notes-submit-button"
          >
            {busy ? <Loader2 size={16} className="spin" /> : <Mail size={16} />}
            {t.submit}
          </button>
          <p className="monograph-note">{t.note}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FieldNotesSubscribeModal;
